import {
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { type ReactNode } from 'react';

import TablePaginationActions from './TablePagination';

export interface GridColDef<T = Record<string, any>> {
  field: string;
  headerName: string | ReactNode;
  width?: number | string;
  description?: string;
  renderCell?: (value: any, row: T) => React.ReactNode;
  textAlign?: 'left' | 'right' | 'center';
  renderLoading?: () => ReactNode;
}

export interface TableProps<T = Record<string, any>> {
  columns: GridColDef<T>[];
  data: T[];
  total: number;
  page: number;
  size: number;
  onChangePage: (newPage: number) => void;
  onChangePageSize: (newPage: number) => void;
  loading?: boolean;
}

const StyledTableCell = styled(TableCell)({
  border: `1px solid ${alpha('#ffffff', 0.15)}`,
  borderLeft: 0,
  ':first-of-type': {
    borderLeft: `1px solid ${alpha('#ffffff', 0.15)}`,
  },
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const renderTypography = ({ text }: { text: string }) => (
  <Typography className='text-body_m text-neutral-90 font-gotham'>{text ? text : null}</Typography>
);

const BasicTable = <T extends Record<string, any>>(props: TableProps<T>) => {
  const { columns, data, total = 0, page = 0, size = 10, onChangePage, onChangePageSize, loading = false } = props;

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    onChangePage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChangePageSize(parseInt(event.target.value, 10));
    onChangePage(0);
  };

  const renderLoading = () =>
    new Array(10).fill(1).map((_i, idx: number) => (
      <TableRow
        sx={{
          '& td': {
            padding: '12px 16px',
          },
          height: '64px',
        }}
        key={idx}>
        {columns.map(col => {
          const key: string = col.field;
          const loadSkeleton = col.renderLoading;
          return (
            <StyledTableCell sx={{ width: col.width, maxWidth: col.width, textAlign: col.textAlign }} key={key}>
              {loadSkeleton ? (
                loadSkeleton()
              ) : (
                <Skeleton variant='text' sx={{ fontSize: '24px', backgroundColor: alpha('#ffffff', 0.1) }} />
              )}
            </StyledTableCell>
          );
        })}
      </TableRow>
    ));

  const renderEmpty = () => (
    <TableRow>
      <StyledTableCell
        colSpan={100}
        style={{ width: '100%', padding: 0, borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px' }}>
        <Stack width='100%' height={244} justifyContent='center' alignItems='center'>
          <Typography mb={'8px'} color='white' className='text-head_xs font-gotham'>
            No OTC sales to display
          </Typography>
          <Typography color='#9C9BA3' className='text-body_m font-gotham'>
            Create an OTC sale for a specific node buyer.
          </Typography>
        </Stack>
      </StyledTableCell>
    </TableRow>
  );

  const renderRow = () =>
    data && data.length > 0
      ? data.map((row, idx: number) => (
          <TableRow
            sx={{
              '& td': {
                padding: '12px 16px',
              },
              height: '64px',
            }}
            key={idx}>
            {columns.map(col => {
              const key: string = col.field;
              const lastRow = data.length - 1 === idx;
              return (
                <StyledTableCell
                  sx={{
                    width: col.width,
                    maxWidth: col.width,
                    textAlign: col.textAlign,
                    ...(lastRow
                      ? {
                          ':first-of-type': {
                            borderBottomLeftRadius: '6px',
                          },
                          ':last-of-type': {
                            borderBottomRightRadius: '6px',
                          },
                        }
                      : null),
                  }}
                  key={key}>
                  {col.renderCell
                    ? col.renderCell(row[key], row)
                    : renderTypography({
                        text: row[key] as string,
                      })}
                </StyledTableCell>
              );
            })}
          </TableRow>
        ))
      : renderEmpty();

  return (
    <TableContainer>
      <Table
        sx={{
          minWidth: 700,
          '& .MuiTableCell-head': { lineHeight: '28px' },
          borderCollapse: 'separate',
          borderSpacing: 0,
        }}
        aria-label='customized table'>
        <TableHead
          sx={{
            borderRadius: '8px',
            color: alpha('#ffffff', 0.15),
            border: 0,
            backgroundColor: alpha('#ffffff', 0.06),
          }}>
          <TableRow
            sx={{
              '& th': {
                padding: '12px 16px',
              },
            }}>
            {columns.map(col => (
              <StyledTableCell
                sx={{
                  width: col.width,
                  maxWidth: col.width,
                  textAlign: col.textAlign,
                  borderBottom: 0,
                  ':first-of-type': {
                    borderTopLeftRadius: '6px',
                  },
                  ':last-of-type': {
                    borderTopRightRadius: '6px',
                  },
                }}
                key={col.field}>
                {typeof col.headerName === 'string' ? renderTypography({ text: col.headerName }) : col.headerName}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{loading ? renderLoading() : renderRow()}</TableBody>
        <TableFooter sx={{ borderBottom: 0 }}>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              colSpan={columns?.length || 5}
              count={total}
              rowsPerPage={size}
              page={page}
              slotProps={{
                select: {
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  MenuProps: {
                    slotProps: {
                      paper: {
                        sx: {
                          backgroundColor: '#060912',
                          color: 'white',
                          '& .MuiMenu-list': {
                            padding: 0,
                          },
                        },
                      },
                    },
                  },
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={props => <TablePaginationActions {...props} />}
              sx={{
                borderBottom: 0,
                color: 'white',
                fontFamily: 'var(--font-circular-std)',
                '& .MuiTablePagination-selectIcon': {
                  color: 'white',
                },
              }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default BasicTable;

BasicTable.displayName = 'BasicTable';
